import {
  type ForwardedRef,
  forwardRef,
  type InputHTMLAttributes,
  useContext,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";
import { InputContext } from "./InputContext";
import { Icon as IconBase } from "../Icon/Icon";
import type { IconType } from "../Icon";
import { Switch as SwitchRoot } from "../Switch/Switch";

export const InputSwitch = (props: {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}) => {
  return (
    <SwitchRoot
      checked={props.checked}
      onChange={props.onChange}
      disabled={props.disabled}
      className={props.className}
    />
  );
};

export const InputCheckbox = forwardRef(
  (
    {
      variant = "default",
      checked: controlledChecked,
      onChange,
      containerClassName,
      ...props
    }: InputHTMLAttributes<HTMLInputElement> & {
      variant?: "default" | "circular";
      checked?: boolean;
      containerClassName?: string;
    },
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const isControlled = controlledChecked !== undefined;
    const [internalChecked, setInternalChecked] = useState(
      props.defaultChecked,
    );
    const checked = isControlled ? controlledChecked : internalChecked;

    return (
      <div
        className={twMerge(
          "relative flex items-center justify-center",
          containerClassName,
        )}
      >
        <input
          {...props}
          ref={ref}
          type="checkbox"
          checked={checked}
          onChange={(e) => {
            if (!isControlled) setInternalChecked(e.target.checked);
            onChange?.(e);
          }}
          className={twMerge(
            "h-5 w-5 cursor-pointer border border-zinc-300 transition-all checked:bg-zinc-800 focus:ring-0 focus:ring-offset-0 dark:border-zinc-700 dark:checked:bg-white",
            variant === "circular" ? "rounded-full" : "rounded-md",
            props.className,
          )}
        />
      </div>
    );
  },
);

export const InputLabel = (props: {
  children: React.ReactNode;
  className?: string;
}) => {
  const { inputId } = useContext(InputContext);
  return (
    <label
      htmlFor={inputId}
      className={twMerge("text-base font-semibold", props.className)}
    >
      {props.children}
    </label>
  );
};

export const InputNote = (props: {
  children: React.ReactNode;
  className?: string;
  type?: "error" | "info";
}) => {
  return (
    <p
      className={twMerge(
        "text-[11px] leading-relaxed font-medium",
        props.type === "error"
          ? "text-red-600 dark:text-red-500"
          : "text-zinc-500 dark:text-zinc-400",
        props.className,
      )}
    >
      {props.children}
    </p>
  );
};

export const InputIcon = ({
  icon,
  className,
}: {
  icon: IconType;
  className?: string;
}) => {
  return (
    <IconBase
      icon={icon}
      size={0.8}
      className={twMerge("text-zinc-400 dark:text-zinc-500", className)}
    />
  );
};

export const InputLengthIndicator = (props: {
  currentLength: number;
  maxLength?: number;
  minLength?: number;
  className?: string;
}) => {
  return (
    <div
      className={twMerge(
        "flex w-full text-[10px] font-bold tracking-widest text-zinc-400 uppercase",
        props.className,
      )}
    >
      {props?.minLength && props.currentLength < props.minLength && (
        <div className="mr-auto">
          You need {props.minLength - props.currentLength} more characters.
        </div>
      )}
      {props.maxLength && (
        <div className="ml-auto">
          {props.currentLength}/{props.maxLength}
        </div>
      )}
    </div>
  );
};

export { InputContainer } from "./InputContainer";
export { InputSelect } from "./InputSelect";
export { InputOption } from "./InputOption";

InputCheckbox.displayName = "InputCheckbox";
